import CustomItemModal from "@/components/CustomItemModal/CustomItemModal";
import LineItemInsert from "@/components/OrderInserts/LineItemInsert";
import { TrashIcon } from "@/components/Icons/Trash";
import { LineItem } from "@/utils/types/types";
import { ActionIcon, Button, Loader, Textarea, Tooltip } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { CaretDown, ImagesSquare, PencilSimple } from "@phosphor-icons/react";
import { saveSpecTemplate } from "@/stores/specTemplate";
import { useState } from "react";

const formatPrice = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
}).format;

type CartLineItemProps = {
  lineItem: LineItem;
  onUpdate?: (payload: { id: string; quantity: number; note: string }) => void;
  onDelete?: (id: LineItem["id"]) => void;
  onSaved?: () => void | Promise<void>;
  locked?: boolean;
  deleting?: boolean;
  pendingDelete?: boolean;
  isAdmin?: boolean;
  shareSecret?: string;
  /** Show "Upload insert or COA" on this line (cart + checkout). */
  showInsert?: boolean;
};

export function CartLineItem({
  lineItem,
  onUpdate,
  onDelete,
  onSaved,
  locked,
  deleting = false,
  pendingDelete = false,
  isAdmin = false,
  shareSecret,
  showInsert = false,
}: CartLineItemProps) {
  const [data, setData] = useState({
    note: lineItem.note,
    quantity: lineItem.quantity,
  });
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [detailsExpanded, { toggle: toggleDetails }] = useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  function updateData(partial: Partial<typeof data>) {
    const newData = { ...data, ...partial };
    setData(newData);
    onUpdate?.({ id: lineItem.id, ...newData });
  }
  const itemJson = lineItem?.itemJson;

  const cartData = itemJson?.cartData || {};
  const [sizeDescription, ...otherDescriptions] = [
    cartData.staticField1,
    cartData.staticField2,
    cartData.staticField3,
    cartData.staticField4,
    ...(cartData.conditionalFields || []),
  ];

  const url = itemJson?.previewImageUrl || itemJson?.imageUrl;
  const uploadedFileName = itemJson?.fileName || "Unnamed upload";
  const count = data.quantity;
  const price = Number(itemJson?.price || 0);
  const totalPrice = count * price;

  const filteredDescriptions = otherDescriptions.filter(Boolean);
  // On mobile the descriptions are collapsed behind a "View Details" toggle.
  const showDetails = !isMobile || detailsExpanded;
  // Insert / COA for this print (2026-10-18).
  const insert =
    showInsert && lineItem.cartId ? (
      <LineItemInsert
        cartId={lineItem.cartId}
        lineItemId={lineItem.id}
        locked={locked}
      />
    ) : null;

  return (
    <div>
      <CustomItemModal
        opened={editModalOpened}
        onClose={closeEditModal}
        onSave={async () => {
          await onSaved?.();
          closeEditModal();
        }}
        cartId={lineItem.cartId}
        lineItem={lineItem}
      />
      <div
        className={`grid grid-cols-[48px_1fr] gap-3 transition-opacity md:grid-cols-[2fr_4fr_3fr] ${
          pendingDelete ? "opacity-45" : ""
        }`}
      >
        <div className="flex flex-col gap-3">
          <img src={url} alt="preview" className="shadow-sm" />
          {!isMobile && (
            <Button.Group>
              <Button
                size="compact-sm"
                variant="default"
                onClick={() => updateData({ quantity: data.quantity - 1 })}
                disabled={data.quantity <= 0 || locked}
                style={{
                  background: "#fff",
                  color: "#191919",
                  border: "1px solid #00000014",
                }}
              >
                &minus;
              </Button>
              <Button
                size="compact-sm"
                variant="default"
                className="pointer-events-none"
                style={{
                  background: "#00000014",
                  color: "#191919",
                  borderBlock: "1px solid #00000014",
                }}
              >
                {data.quantity}
              </Button>
              <Button
                size="compact-sm"
                variant="default"
                onClick={() => updateData({ quantity: data.quantity + 1 })}
                disabled={locked}
                style={{
                  background: "#fff",
                  color: "#191919",
                  border: "1px solid #00000014",
                }}
              >
                +
              </Button>
            </Button.Group>
          )}
        </div>
        <div className="flex gap-3">
          <div className="flex min-w-0 flex-col content-start gap-3">
            {pendingDelete && (
              <p className="text-[11px] font-[700] uppercase tracking-wide text-[#C1332A]">
                Will be deleted after cart update
              </p>
            )}

            <div className="flex gap-3">
              <p className="text-[16px] font-[600] leading-[18px]">
                {sizeDescription}
                <span className="block max-w-[320px] truncate text-[12px] leading-none text-neutral-500">
                  {uploadedFileName}
                </span>
              </p>
              {isMobile && (
                <ActionIcon
                  onClick={() => {
                    if (isAdmin && lineItem.adminCustom) {
                      openEditModal();
                    } else {
                      const params = new URLSearchParams({
                        editItem: lineItem.id,
                      });
                      if (shareSecret && lineItem.cartId) {
                        params.set("cartId", lineItem.cartId);
                        params.set("secret", shareSecret);
                      }
                      window.location.href = `/visualizer?${params.toString()}`;
                    }
                  }}
                  variant="white"
                  color="#4AA0A0"
                  disabled={locked || (!isAdmin && !!lineItem.adminCustom)}
                  style={{
                    border: "1px solid #4AA0A040",
                    borderRadius: "8px",
                    flexShrink: "0",
                  }}
                  h={28}
                  w={28}
                >
                  <PencilSimple size={16} />
                </ActionIcon>
              )}
            </div>
            {isMobile && filteredDescriptions.length > 0 && (
              <button
                type="button"
                onClick={toggleDetails}
                className="flex w-fit items-center gap-1"
                style={{
                  color: "#636363",
                  textAlign: "center",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {detailsExpanded ? "Hide Details" : "View Details"}
                <CaretDown
                  size={14}
                  className={`transition-transform ${
                    detailsExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
            {showDetails && filteredDescriptions.length > 0 && (
              <ul
                className="flex flex-col gap-1 pl-3"
                style={{ listStyleType: "disc" }}
              >
                {filteredDescriptions.map(description => (
                  <li
                    key={description}
                    className={
                      // Multi-line bullets (multi-window "Windows:" list) keep their line breaks.
                      String(description).includes("\n")
                        ? "whitespace-pre-line text-[13px] leading-snug text-neutral-500"
                        : "text-[13px] leading-none text-neutral-500"
                    }
                  >
                    {description}
                  </li>
                ))}
              </ul>
            )}

            {itemJson.description && (
              <div>
                {!!(
                  itemJson?.sizing?.dimensions?.overallWidth ||
                  itemJson?.sizing?.dimensions?.overallHeight
                ) && (
                  <ul className="cart-description list-disc pl-3">
                    <li className="text-[13px] leading-none text-neutral-500">
                      Overall Dimensions:{" "}
                      {itemJson.sizing.dimensions.overallWidth}
                      "&nbsp;&times;&nbsp;
                      {itemJson.sizing.dimensions.overallHeight}"
                    </li>
                  </ul>
                )}
                <div
                  className="cart-description"
                  dangerouslySetInnerHTML={{ __html: itemJson.description }}
                />
              </div>
            )}
            <div className="flex items-end gap-2 leading-none">
              <p className="text-[18px] font-[700] text-[#161D1D]">
                {formatPrice(totalPrice)}
              </p>
              <p className="text-[14px] leading-[18px] text-neutral-500">
                Price
              </p>
              {count > 1 && (
                <p className="text-[12px] text-neutral-500">
                  ({formatPrice(price)}/per)
                </p>
              )}
            </div>
            {isMobile && (
              <div
                style={{
                  display: "flex",
                  minHeight: "28px",
                  padding: "5px 10px",
                  alignItems: "flex-start",
                  alignSelf: "stretch",
                  gap: "6px",
                  borderRadius: "4px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  background: "#FFF",
                }}
              >
                <span
                  style={{
                    color: "#636363",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Item Notes:
                </span>
                <Textarea
                  variant="unstyled"
                  disabled={locked}
                  value={data.note}
                  onChange={event =>
                    updateData({ note: event.currentTarget.value })
                  }
                  autosize
                  minRows={1}
                  maxRows={4}
                  style={{ flex: 1 }}
                  styles={{
                    input: {
                      padding: 0,
                      lineHeight: "18px",
                      fontSize: "14px",
                    },
                  }}
                />
              </div>
            )}
            {isMobile && insert}
          </div>
        </div>

        <div className="flex items-end justify-between md:flex-col">
          <div className="flex flex-col gap-2 md:flex-row">
            {!isMobile && (
              <ActionIcon
                onClick={() => {
                  if (isAdmin && lineItem.adminCustom) {
                    openEditModal();
                  } else {
                    const params = new URLSearchParams({
                      editItem: lineItem.id,
                    });
                    if (shareSecret && lineItem.cartId) {
                      params.set("cartId", lineItem.cartId);
                      params.set("secret", shareSecret);
                    }
                    window.location.href = `/visualizer?${params.toString()}`;
                  }
                }}
                variant="white"
                color="#4AA0A0"
                disabled={locked || (!isAdmin && !!lineItem.adminCustom)}
                style={{ border: "1px solid #4AA0A040", borderRadius: "8px" }}
                h={40}
                w={40}
              >
                <PencilSimple size={20} />
              </ActionIcon>
            )}
            {/* "Same specs, new image" — customers too since 2026-09-11 (was
                admin-only to start). zIndex: the cart drawer sits at z-8000
                (AppLayout), above Mantine's default tooltip portal — the
                tooltip rendered behind the drawer and was never visible. */}
            {!lineItem.adminCustom && (
              <Tooltip
                label="Same specs, new image — start another piece with these specs; you'll choose the image and size."
                withArrow
                multiline
                w={260}
                zIndex={9000}
              >
                <ActionIcon
                  onClick={() => {
                    saveSpecTemplate(lineItem);
                    window.location.href = "/upload";
                  }}
                  variant="white"
                  color="#4AA0A0"
                  disabled={locked}
                  style={{ border: "1px solid #4AA0A040", borderRadius: "8px" }}
                  h={40}
                  w={40}
                >
                  <ImagesSquare size={20} />
                </ActionIcon>
              </Tooltip>
            )}
            <ActionIcon
              variant="white"
              color="#C1332A"
              onClick={() => onDelete?.(lineItem.id)}
              disabled={locked}
              loading={deleting}
              size={32}
              style={{
                border: "1px solid #FFDDDB",
                borderRadius: "8px",
                width: isMobile ? "32px" : "40px",
                height: isMobile ? "32px" : "40px",
                minWidth: "32px",
                minHeight: "32px",
              }}
            >
              {deleting ? (
                <Loader size="sm" color="#C1332A" />
              ) : (
                <TrashIcon size={isMobile ? 16 : 20} />
              )}
            </ActionIcon>
          </div>
          {!isMobile && (
            <Textarea
              disabled={locked}
              value={data.note}
              onChange={event =>
                updateData({ note: event.currentTarget.value })
              }
              label="Item Notes:"
              rows={2}
              style={{ textAlign: "right" }}
            />
          )}
          {!isMobile && insert}
        </div>
        {isMobile && (
          <Button.Group>
            <Button
              variant="default"
              onClick={() => updateData({ quantity: data.quantity - 1 })}
              disabled={data.quantity <= 0 || locked}
              style={{
                background: "#fff",
                color: "#191919",
                border: "1px solid #00000014",
                height: "32px",
                width: "32px",
                minWidth: "32px",
                padding: 0,
                fontSize: "16px",
              }}
            >
              &minus;
            </Button>
            <Button
              variant="default"
              className="pointer-events-none"
              style={{
                background: "#00000014",
                color: "#191919",
                borderBlock: "1px solid #00000014",
                height: "32px",
                width: "32px",
                minWidth: "32px",
                padding: 0,
                fontSize: "16px",
              }}
            >
              {data.quantity}
            </Button>
            <Button
              variant="default"
              onClick={() => updateData({ quantity: data.quantity + 1 })}
              disabled={locked}
              style={{
                background: "#fff",
                color: "#191919",
                border: "1px solid #00000014",
                height: "32px",
                width: "32px",
                minWidth: "32px",
                padding: 0,
                fontSize: "16px",
              }}
            >
              +
            </Button>
          </Button.Group>
        )}
      </div>
    </div>
  );
}
